/**
 * The Data API seam.
 *
 * GraphQL, but it does not look like GraphQL from the outside. Each Data API operation
 * is exposed as a typed method that sends a generated, depth-bounded full-selection
 * document, so a caller writes `mountaya.data.directions(input)` and never builds a query:
 * neither a REST facade over the Data API nor a bare GraphQL client.
 *
 * Three things about the Data API that a generic GraphQL client gets wrong:
 *
 * 1. **A failure can arrive with any HTTP status.** The API answers a failure as a GraphQL
 *    `{errors: […]}` body under that failure's own status, so an entitlement failure is a
 *    402 carrying a GraphQL envelope. Reading only the status loses the message; reading
 *    only the body misses that it was a 402. A non-2xx is classified by the same ladder as
 *    a REST failure, with the envelope's entries, so a 401 is an authentication error and a
 *    402 one of the agreement's classes on every surface alike.
 * 2. **A 200 can still be a failure.** GraphQL puts errors in the body. A client that
 *    branches on `response.ok` alone reports success and hands back `undefined`.
 * 3. **The document is the bulk of the request, and it does not have to be sent.** A full
 *    selection is kilobytes of upload; the variables are bytes. The API runs Automatic
 *    Persisted Queries, so the document travels once per hash and every call after that
 *    sends the hash, on the surface with the tightest per-minute limit.
 *
 * All three are handled here so no operation has to. So is the date rule every REST body
 * follows: each `DateTime` the operation's mask names decodes to a `Date`, so
 * `exportItinerary`'s `expiresAt` is the instant its type says rather than the string the wire
 * carries, and no other string does, so a `filename` that is a timestamp stays a filename.
 */

import { type DateMask, decodeDates, parseJsonWithDates } from "./dates.js";
import {
  answerDetailsOf,
  type ErrorEntry,
  entriesOf,
  errorForStatus,
  errorFromBody,
  failureMessage,
  MountayaDataError,
  metadataOf,
  parseRetryAfter,
  statesErrors,
} from "./errors.js";
import { parseSuccessBody, readFailureText, readText } from "./responseBody.js";
import type { DataOptions } from "./selection.js";
import type { MountayaTransport, RequestOptions } from "./transport.js";

/** What a Data API call reads of its options bag: a REST call's, and its own `timeoutMs`. */
type CallOptions = RequestOptions & Pick<DataOptions<unknown>, "timeoutMs">;

/**
 * DATA_API_BUDGET_MS is the most the Data API spends on any operation before it answers a 504
 * itself, in milliseconds. The SDL's introduction states it once for every operation, and the
 * contract's model carries it on each as `timeoutMs`, which `scripts/contract-checkout.test.mjs`
 * holds this value to. Each attempt waits it plus the transport's margin, so the API's own 504
 * arrives before the client gives up on it.
 */
export const DATA_API_BUDGET_MS = 30_000;

/**
 * The body of a GraphQL response. Its `errors` are the API's error entries, with the input
 * field a validation failure is about under `extensions.path`.
 */
interface GraphQLResponse<T> {
  readonly data?: Record<string, T> | null | undefined;
  readonly errors?: readonly ErrorEntry[] | undefined;
  /** Where a failure's metadata rides, as on REST, for {@link metadataOf} to read. */
  readonly extensions?: unknown;
}

/**
 * PersistedQuery is the `extensions.persistedQuery` member of an APQ request.
 *
 * `version` rides on the wire rather than being something the server assumes: a server that
 * met a version it did not implement would say so by name, which is what makes a future
 * revision of the protocol a visible refusal instead of a silent misreading of the hash.
 */
interface PersistedQuery {
  readonly sha256Hash: string;
  readonly version: 1;
}

/**
 * GraphQLRequest is the POST body.
 *
 * Both `query` and `extensions` are optional, and which pair is present is the whole APQ
 * handshake: the hash alone on an optimistic call, the document alongside the hash on the
 * one call that registers it, the document alone where APQ is not available.
 */
interface GraphQLRequest {
  readonly query?: string | undefined;
  readonly variables: { readonly input: unknown };
  readonly extensions?: { readonly persistedQuery: PersistedQuery } | undefined;
}

/**
 * Attempt is one round trip: the response, and the body already read off it.
 *
 * The body travels alongside the response rather than being fetched from it on demand,
 * because by this point it has been consumed (see {@link execute}), so there is nothing
 * left downstream to read a second time.
 */
interface Attempt<TResult> {
  readonly response: Response;
  /** The body as the text it arrived as, for the classifier a failure with no entries falls to. */
  readonly raw: string;
  /**
   * The decoded envelope, or nothing for an empty body or a failure's body that is not JSON. A
   * success's body that is not JSON is a connection failure, and never reaches an attempt.
   */
  readonly envelope: GraphQLResponse<TResult> | undefined;
  /**
   * The envelope's error entries as {@link entriesOf} reads them: each member of its `errors`
   * that is an object carrying a message.
   */
  readonly entries: readonly ErrorEntry[];
}

/**
 * Automatic Persisted Queries, as the Data API's server speaks them.
 *
 * A call sends `extensions.persistedQuery.sha256Hash` and no document. The server looks the
 * hash up in its cache and either runs the document it already holds, or answers
 * `PersistedQueryNotFound`, at which point the document is sent ONCE, alongside the same
 * hash, which both satisfies that call and seeds the cache for every call after it.
 *
 * Two properties make the optimism worth it. A probe that misses costs one round trip on a
 * request that had almost no body, and it happens once per document per cache lifetime
 * rather than once per call. And the hash is over the exact bytes of the document, which the
 * server recomputes and compares before storing, so a hash and a document can never end up
 * describing different queries.
 *
 * The two pieces of state below are kept at two different grains, because they answer two
 * different questions. A digest is a property of the BYTES, so it is memoised per process: a
 * second client pointed anywhere would only recompute what the first already knows. Support
 * is a property of the SERVER, so it is remembered per transport: an application holding a
 * client against production and a second against a local stack would otherwise let one
 * server's refusal speak for both, and the second client would send full documents forever
 * with nothing to say why.
 */

/**
 * The transports whose server has answered that it does not implement APQ at all.
 *
 * A WeakSet rather than a flag on the transport: this is a fact the GraphQL seam learned and
 * caches, not part of a transport's contract, and putting it on the object would make it
 * something every other caller of `MountayaTransport` can see and set. Weak, so a client that goes
 * out of scope takes its entry with it: the set is keyed by a long-lived object a consumer
 * owns, which is exactly the shape that leaks when held strongly.
 *
 * Membership is one-way on purpose. A server that answered `PersistedQueryNotSupported` has
 * told us about its build, not about its mood, so re-probing it would cost a wasted round
 * trip per document forever to learn the same thing.
 */
const apqUnsupported = new WeakSet<MountayaTransport>();

/**
 * The most distinct documents whose digests are kept.
 *
 * The generated full selections are a closed set and would need no bound at all. `select` is
 * what opens it: a selection may be built at runtime (from a config file, a query string, or
 * a model's answer), so the documents a long-lived process prints are bounded only by what
 * its callers ask for. The cap sits far above any real application's operation count and
 * turns a map that only grows into a warm cache that occasionally misses.
 */
const MAX_MEMOISED_DIGESTS = 256;

/**
 * Document digests, memoised by the exact bytes that were hashed.
 *
 * The PROMISE is memoised rather than the string it resolves to. `crypto.subtle` is async, so
 * caching only the settled value leaves every caller that arrived before the first one
 * finished to start a digest of its own, which is the burst at application start, exactly
 * where the memo is meant to help.
 */
const documentHashes = new Map<string, Promise<string>>();

/**
 * subtleCrypto resolves the Web Crypto digest implementation, or nothing where there is none.
 *
 * There are two ways to have none and neither is exotic: `crypto.subtle` is undefined outside
 * a secure context, which is any page served over plain HTTP, and it is absent altogether in
 * React Native, which this package supports. Neither is an error: APQ is an optimization, and
 * the document is still perfectly sendable.
 *
 * The widened binding is what makes the check expressible at all. `lib.dom` declares `crypto`
 * as always present, so comparing `globalThis.crypto?.subtle` against `undefined` is a
 * comparison TypeScript rejects as impossible, while at runtime it is the case this whole
 * path exists for.
 */
const subtleCrypto = (): SubtleCrypto | undefined => {
  const runtime: { readonly crypto?: Crypto | undefined } = globalThis;

  return runtime.crypto?.subtle;
};

/**
 * hashDocument computes the lowercase hex SHA-256 of a document, memoised, or nothing where
 * there is no digest implementation to compute it with.
 *
 * The encoding is not an implementation detail: the server recomputes this digest from the
 * document it stored and refuses a mismatch, so UTF-8 bytes and lowercase hex are a contract.
 * That is also why there is no hand-rolled fallback for a runtime without Web Crypto: a
 * substitute digest would not be slower, it would be wrong.
 */
export const hashDocument = (query: string): Promise<string> | undefined => {
  const memoised = documentHashes.get(query);
  if (memoised !== undefined) {
    return memoised;
  }

  const subtle = subtleCrypto();
  if (subtle === undefined) {
    return undefined;
  }

  const digest = subtle
    .digest("SHA-256", new TextEncoder().encode(query))
    .then((bytes) =>
      Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join(""),
    );

  // A rejected digest is dropped rather than kept: memoising it would turn one failure into a
  // permanent opt-out of APQ for that document, and recomputing costs a hash rather than a
  // round trip. The handler also settles this branch of the promise, so the rejection the
  // caller catches is not additionally reported as unhandled.
  digest.catch(() => documentHashes.delete(query));

  if (documentHashes.size >= MAX_MEMOISED_DIGESTS) {
    // A Map iterates in insertion order, so this is the least recently added entry.
    const oldest = documentHashes.keys().next().value;
    if (oldest !== undefined) {
      documentHashes.delete(oldest);
    }
  }

  documentHashes.set(query, digest);

  return digest;
};

/**
 * persistedQueryFor builds the APQ extension for a document, or nothing when APQ is not
 * available for this call.
 *
 * It never throws. Every way it can decline (a server that has refused the protocol, a
 * runtime with no Web Crypto, a digest that failed) leaves the caller with a document it can
 * send in full, so none of them is a failure a consumer should ever see.
 *
 * The transport is the first argument because the refusal it checks is that server's, not
 * this process's: see {@link apqUnsupported}.
 */
const persistedQueryFor = async (
  transport: MountayaTransport,
  query: string,
): Promise<PersistedQuery | undefined> => {
  if (apqUnsupported.has(transport)) {
    return undefined;
  }

  const digest = hashDocument(query);
  if (digest === undefined) {
    return undefined;
  }

  try {
    return { sha256Hash: await digest, version: 1 };
  } catch {
    return undefined;
  }
};

/** Reports whether any entry carries this exact message. */
const named = (entries: readonly ErrorEntry[], message: string): boolean =>
  entries.some((entry) => entry.message === message);

/** Reports whether any entry carries this exact `extensions.code`. */
const coded = (entries: readonly ErrorEntry[], code: string): boolean =>
  entries.some((entry) => entry.extensions?.code === code);

/**
 * Reports whether any ONE entry carries both this exact `extensions.code` and this exact message.
 * Both are asked of the same entry: a code alone, or a message alone, is another answer.
 */
const codedAndNamed = (entries: readonly ErrorEntry[], code: string, message: string): boolean =>
  entries.some((entry) => entry.extensions?.code === code && entry.message === message);

/** What an APQ probe's answer asks for, when it asks for anything beyond a result. */
export type ApqRefusal = "register" | "unsupported";

/**
 * apqRefusal reads a probe's answer for the two things it can mean other than success.
 *
 * `register`: the server does not hold this document yet and is asking for it. This is the
 * ordinary case, once per document per cache lifetime, and it arrives as a 200 carrying
 * `errors`, so it has to be recognized before the rule that a 200 with errors is a failure.
 * A server may send the message and the code, or the message alone as Apollo Server does, and
 * both are accepted so the seam is not pinned to one server implementation.
 *
 * `unsupported`: the server does not implement APQ at all. Apollo Server says so by name. The
 * Data API's server has no such answer: with the extension turned off it finds no operation in
 * a body with no query in it, and answers a 422 whose entry is coded `GRAPHQL_VALIDATION_FAILED`
 * with the message `no operation provided`. Reading that as an ordinary error would break every
 * Data API call the moment APQ is turned off, which is a server-side flag that looks harmless.
 * It is only readable this way because the document is omitted on this attempt and on no other,
 * and only that code and that message on one entry read so: the same answer to a request that
 * carried its document, the same code with any other message, and a parse failure are genuine
 * failures and reach the caller untouched.
 *
 * An answer that carries both reads as `unsupported`.
 */
export const apqRefusal = (entries: readonly ErrorEntry[]): ApqRefusal | undefined => {
  // The declined protocol is read first: an answer that carries both refusals is from a server
  // that would refuse a registration too, so registering would spend a round trip for nothing.
  if (
    named(entries, "PersistedQueryNotSupported") ||
    coded(entries, "PERSISTED_QUERY_NOT_SUPPORTED") ||
    codedAndNamed(entries, "GRAPHQL_VALIDATION_FAILED", "no operation provided")
  ) {
    return "unsupported";
  }

  if (named(entries, "PersistedQueryNotFound") || coded(entries, "PERSISTED_QUERY_NOT_FOUND")) {
    return "register";
  }

  return undefined;
};

/**
 * execute performs one Data API round trip and reads its body.
 *
 * The body is read ONCE, as text, and everything else is parsed from that string.
 *
 * A `response.clone().json()` is the shape to avoid, and the clone is the reason: on the
 * success path (every call that works) the ORIGINAL body is never read, so undici tees the
 * stream and buffers it with no reader draining the other side, and the only branch that would
 * touch the original is the classifier fallback in {@link unwrap}. Reading text once and
 * rebuilding a Response for that one branch keeps the classifier's input intact and leaves
 * nothing undrained.
 */
const execute = async <TResult>(
  transport: MountayaTransport,
  operationId: string,
  body: GraphQLRequest,
  options?: CallOptions,
): Promise<Attempt<TResult>> => {
  const response = await transport.requestRaw({
    body,
    // Every Data API operation is a query. The POST is GraphQL's transport, not a
    // mutation, so these are safe to retry, and they are the operations that most need
    // it, on the surface with the tightest per-minute limit.
    idempotent: true,
    method: "POST",
    operationId,
    path: "/graphql",
    // The same fact, told to the credential rules that read it: a refusal names a read as a read,
    // not as a write. This is the ONE place the method implies the wrong answer, which is why the
    // flag is declared here and nowhere else.
    read: true,
    // The Data API requires a session token beside the publishable key on every request, and
    // answers one without it with a 401. The SDL has no `security` to generate this from, so
    // it is stated here, once, for every operation: the declaration a REST operation's
    // `security` produces, read by the same refusal. No operation here accepts a share token,
    // which the absence of `acceptsShareToken` says.
    requiresSession: true,
    retry: options?.retry,
    // The Data API states one budget for every operation rather than one per operation, so it
    // is stated here, once, as a REST operation's `x-timeout-ms` is stated on its descriptor.
    serverBudgetMs: DATA_API_BUDGET_MS,
    signal: options?.signal,
    surface: "data",
    timeoutMs: options?.timeoutMs,
  });

  // A success's body is the answer, so one that breaks off, or one that is not JSON, is a
  // connection failure, and a caller's abort is theirs; reading either as empty would report an
  // answer the API never gave. A failure's body is only a better message for an error its
  // status already classifies, so it is read as the shared classifier reads one: under the
  // bounds a refusal's body is read under, and tolerated when it does not arrive or does not
  // parse. The caller's abort ends either read, since a caller who left waits for neither.
  const raw = response.ok
    ? await readText(response, {
        idempotent: true,
        operationId,
        signal: options?.signal,
      })
    : await readFailureText(response, options?.signal);
  const envelope = ((): GraphQLResponse<TResult> | undefined => {
    // Parsed with no mask: the instants are the result's, which unwrap decodes once it has the
    // result in hand, and a failure's envelope holds none, as a REST failure's does not.
    //
    // The two casts are the wire's boundary: the body is typed as the envelope the Data API
    // answers and is not validated against it. Its `errors` are read through `entriesOf`,
    // which checks them, and its `data` is the operation's own type, as the contract states it.
    if (response.ok) {
      return parseSuccessBody(raw, response.status, { operationId, surface: "data" }) as
        | GraphQLResponse<TResult>
        | undefined;
    }

    try {
      return parseJsonWithDates(raw) as GraphQLResponse<TResult> | undefined;
    } catch {
      return undefined;
    }
  })();

  return { entries: entriesOf(envelope), envelope, raw, response };
};

/**
 * unwrap turns one attempt into the operation's result, with each instant `dates` names decoded
 * to a `Date`, or into the typed error the API described.
 */
const unwrap = async <TResult>(
  operationId: string,
  attempt: Attempt<TResult>,
  dates: DateMask | undefined,
): Promise<TResult> => {
  const { entries, envelope, raw, response } = attempt;
  // What the answer names itself by and the limits it was counted against, which every error
  // raised from it carries, a failure under a success status included.
  const details = answerDetailsOf(response.headers);

  if (!response.ok) {
    // A failure carrying a GraphQL envelope: keep the API's message and its code, and classify
    // by the real status through the same ladder a REST failure climbs. The status is the
    // classification (a 401 is an authentication failure and a 429 a spent limit on this surface
    // as on every other), and the code splits a 402, which the Data API reports as a bare
    // PAYMENT_REQUIRED and so lands on the billing class.
    if (entries.length > 0) {
      const retryAfterSeconds = parseRetryAfter(response.headers.get("Retry-After"));

      throw errorForStatus(
        failureMessage(operationId, response.status, entries, {
          requestId: details.requestId,
          retryAfterSeconds,
        }),
        response.status,
        entries,
        { ...details, metadata: metadataOf(envelope), retryAfter: retryAfterSeconds },
      );
    }

    // No entry to read: a transport fault or a bodiless 5xx. The shared classifier takes the
    // text already read with the status and the headers it classifies on, so the failure still
    // lands on the right typed class, whatever the status is.
    throw errorFromBody(raw, response, operationId);
  }

  if (statesErrors(envelope)) {
    // The case a generic client reports as success. The status is 200 and the operation
    // did not happen, which no rung of the ladder describes, so the message names no status.
    // Read off the `errors` member and never off the entries alone: one that holds something
    // and no entry to read still says the operation failed.
    const message = failureMessage(operationId, undefined, entries);
    const summary =
      message === operationId ? `${operationId}: the Data API returned an error` : message;

    throw new MountayaDataError(
      details.requestId === undefined ? summary : `${summary} (request ${details.requestId})`,
      200,
      entries,
      { ...details, metadata: metadataOf(envelope) },
    );
  }

  const data = envelope?.data;
  if (data === undefined || data === null) {
    throw new MountayaDataError(`${operationId}: the Data API returned no data`, 200, [], details);
  }

  // `noUncheckedIndexedAccess` makes this `TResult | undefined`, and a cast would erase it. A
  // contract rename would then return `undefined` typed as the result, and the first sign of it
  // would be a property read on undefined somewhere in the caller. Narrowed here, where the
  // operation name is still in hand to say which field was missing.
  const result = data[operationId];
  if (result === undefined) {
    throw new MountayaDataError(
      `${operationId}: the Data API answered without a '${operationId}' field`,
      200,
      [],
      details,
    );
  }

  // The wire's boundary, as above: the field is typed by the operation that asked for it.
  return decodeDates(result, dates) as TResult;
};

/**
 * runOperation executes one generated Data API document and unwraps its result.
 *
 * The response is keyed by the operation's own field name (`{"directions": {…}}`), so
 * the field is unwrapped here and the caller receives the result type directly.
 *
 * `options` is the caller's options bag, of which this reads the `signal`, the per-call `retry`
 * and the per-call `timeoutMs`. The `select` beside them has already been printed into `query`.
 * Each attempt waits the call's own `timeoutMs`, or else the larger of the client's
 * and {@link DATA_API_BUDGET_MS} plus the transport's margin.
 *
 * `dates` names the instants in the result, as `RequestArgs.dates` does for a REST body: the
 * `DateTime` fields the operation's document can select. Omitted, the result decodes no `Date`.
 */
export const runOperation = async <TResult>(
  transport: MountayaTransport,
  operationId: string,
  query: string,
  input: unknown,
  options?: CallOptions,
  dates?: DateMask,
): Promise<TResult> => {
  const variables = { input };
  const persistedQuery = await persistedQueryFor(transport, query);

  if (persistedQuery === undefined) {
    const attempt = await execute<TResult>(transport, operationId, { query, variables }, options);

    return unwrap(operationId, attempt, dates);
  }

  const probe = await execute<TResult>(
    transport,
    operationId,
    { extensions: { persistedQuery }, variables },
    options,
  );

  const refusal = apqRefusal(probe.entries);
  if (refusal === undefined) {
    return unwrap(operationId, probe, dates);
  }

  if (refusal === "unsupported") {
    apqUnsupported.add(transport);
  }

  // Exactly one more attempt, and it is not a retry of a failure: the server asked for the
  // document, so this is the request it would have run had the hash hit. Registering keeps
  // the hash alongside the document: that pair is what seeds the cache, and dropping it
  // would make every call for the rest of the process a miss. A second refusal is a genuine
  // failure: it goes to the caller rather than round the loop again.
  const registration = await execute<TResult>(
    transport,
    operationId,
    refusal === "register"
      ? { extensions: { persistedQuery }, query, variables }
      : { query, variables },
    options,
  );

  return unwrap(operationId, registration, dates);
};
